import { useShallow } from "zustand/shallow";
import { useUIStore } from "../stores/ui-store";
import { UIState } from "../types/ui-types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createReport, fetchReportById, updateReport } from "../api/report.api";
import {
  CreateReportPayload,
  Report,
  ReportType,
  UpdateReportPayload,
} from "../api/models/report.models";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

interface UseReportsProps {
  reportId?: number;
  disableSearch?: boolean;
  disableCache?: boolean;
}

const useReport = ({
  reportId,
  disableSearch = false,
  disableCache = false,
}: UseReportsProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [bearerToken, companyId, organizationId, toast] = useUIStore(
    useShallow((state: UIState) => [
      state.bearerToken,
      state.companyId,
      state.organizationId,
      state.toastRef,
    ]),
  );

  const queryClient = useQueryClient();

  const query = {
    reportId: reportId as number,
    companyId: companyId as number,
    organizationId: organizationId || undefined,
    bearerToken,
  };

  const { data: reportData, isLoading: isLoadingReport } = useQuery({
    queryKey: ["reports", reportId],
    queryFn: () => fetchReportById(query),
    enabled: !!companyId && !!reportId && !disableSearch,
    ...(disableCache && {
      staleTime: 0,
      gcTime: 0,
      refetchOnMount: "always",
    }),
  });

  const createNewReport = async (
    payload: CreateReportPayload,
    reportType?: ReportType,
  ) => {
    const result = await createNewReportMutation.mutateAsync(payload, {
      onSuccess: (result) => {
        if (reportType === ReportType.EAN) {
          navigate(`/report/${result.id}`);
        }

        if (reportType === ReportType.DETAILED) {
          navigate(`/detailed-report/${result.id}`);
        }

        if (reportType === ReportType.LIGHT) {
          navigate(`/light-report/${result.id}`);
        }
      },
    });

    return result;
  };

  const createNewReportMutation = useMutation({
    mutationFn: (payload: CreateReportPayload) =>
      createReport(companyId as number, payload, bearerToken),
    onError: (error) => {
      console.error("Failed to create report row:", error);
      toast?.current?.show({
        severity: "error",
        summary: t("header-row-error"),
        detail: t("text-failed-to-create-new-report"),
        life: 3000,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["reports"],
      });
    },
  });

  const updateExistingReport = async (row: UpdateReportPayload) => {
    const result = await updateExistingReportMutation.mutateAsync(row);
    return result;
  };

  const updateExistingReportMutation = useMutation({
    mutationFn: (row: UpdateReportPayload) =>
      updateReport(companyId as number, row, bearerToken),
    onError: (error) => {
      console.error("Failed to update report row:", error);
      toast?.current?.show({
        severity: "error",
        summary: t("header-row-error"),
        detail: t("text-failed-to-update"),
        life: 3000,
      });
    },
    onSuccess: (result) => {
      /* queryClient.invalidateQueries({
        queryKey: ["reports", result.id],
      }); */
    },
  });

  const isCreatingNewReport = createNewReportMutation.isPending;
  const isUpdatingReport = updateExistingReportMutation.isPending;

  return {
    reportData,
    isLoadingReport,
    createNewReport,
    updateExistingReport,
    isCreatingNewReport,
    isUpdatingReport,
  };
};

export default useReport;
